import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSenderBalance } from "@/hooks/useSenderBalance";
import AdminSidebar from "./components/AdminSidebar";
import AdminOverview from "./components/AdminOverview";
import NoticesTab from "./components/NoticesTab";
import SenderReviewTab from "./components/SenderReviewTab";
import UserManagementTab from "./components/UserManagementTab";
import CreditHistoryTab from "./components/CreditHistoryTab";
import SendStatsTab from "./components/SendStatsTab";
import AdminCarrierNumberTab from "./components/AdminCarrierNumberTab";
import AdminCarrierTestReviewTab from "./components/AdminCarrierTestReviewTab";
import AdminNotificationBell from "./components/AdminNotificationBell";
import TelegramSettings from "./components/TelegramSettings";
import { SenderBalanceIndicator, SenderBalanceBanner } from "./components/SenderBalance";

type TabKey = "overview" | "users" | "charges" | "send-stats" | "notices" | "sender-review" | "carrier-test-review" | "api-keys" | "carrier-numbers" | "system";

const tabLabels: Record<TabKey, string> = {
  overview: "대시보드",
  users: "회원 관리",
  charges: "충전 내역",
  "send-stats": "발송 통계",
  notices: "공지사항 관리",
  "sender-review": "발신번호 인증 심사",
  "carrier-test-review": "3사 테스트 심사",
  "api-keys": "API 키 관리",
  "carrier-numbers": "3사 수신번호 관리",
  system: "시스템 설정",
};

function ApiKeysPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <i className="ri-key-2-line text-4xl text-gray-600 mb-3"></i>
      <p className="text-gray-400 text-base font-medium">API 키 관리</p>
      <p className="text-gray-600 text-sm mt-1">전체 사용자의 API 키 현황을 관리합니다</p>
    </div>
  );
}

function SystemPlaceholder() {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-white font-semibold text-lg">시스템 설정</h2>
        <p className="text-gray-500 text-sm mt-0.5">서비스 운영 환경 설정</p>
      </div>

      {/* 텔레그램 알림 연동 */}
      <TelegramSettings />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[
          { icon: "ri-server-line", title: "서버 설정", desc: "발송 서버 및 큐 설정", color: "text-sky-400" },
          { icon: "ri-shield-check-line", title: "보안 설정", desc: "IP 화이트리스트, 2FA 등", color: "text-emerald-400" },
          { icon: "ri-mail-settings-line", title: "발신번호 정책", desc: "인증 정책 및 심사 기준", color: "text-violet-400" },
          { icon: "ri-coin-line", title: "크레딧 요금표", desc: "SMS/LMS/MMS 단가 설정", color: "text-amber-400" },
          { icon: "ri-database-2-line", title: "데이터 관리", desc: "백업 및 보존 정책", color: "text-pink-400" },
        ].map((item) => (
          <div key={item.title} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 cursor-pointer hover:border-white/[0.15] transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/[0.06]">
                <i className={`${item.icon} text-lg ${item.color}`}></i>
              </div>
              <div>
                <p className="text-white text-sm font-semibold">{item.title}</p>
                <p className="text-gray-500 text-xs">{item.desc}</p>
              </div>
              <i className="ri-arrow-right-s-line text-gray-600 ml-auto"></i>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, isAdmin, roleLoading } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const balance = useSenderBalance();

  // Derive current tab from the pathname
  const getTabFromPath = (): TabKey => {
    const path = location.pathname;
    if (path.startsWith("/admin/users")) return "users";
    if (path.startsWith("/admin/charges")) return "charges";
    if (path.startsWith("/admin/send-stats")) return "send-stats";
    if (path.startsWith("/admin/sender-review")) return "sender-review";
    if (path.startsWith("/admin/carrier-test-review")) return "carrier-test-review";
    if (path.startsWith("/admin/notices")) return "notices";
    if (path.startsWith("/admin/api-keys")) return "api-keys";
    if (path.startsWith("/admin/carrier-numbers")) return "carrier-numbers";
    if (path.startsWith("/admin/system")) return "system";
    return "overview";
  };

  const activeTab = getTabFromPath();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  // 모바일에서 관리자 페이지 접근 차단 → 대시보드로 리다이렉트
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!loading && !roleLoading && isMobile) {
      navigate("/dashboard");
    }
  }, [loading, roleLoading, navigate]);

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-rose-400 text-3xl"></i>
      </div>
    );
  }

  if (!user) return null;

  // 관리자 권한 없으면 접근 차단
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#080812] flex items-center justify-center">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-red-500/10 border border-red-500/20 mx-auto mb-5">
            <i className="ri-shield-cross-line text-3xl text-red-400"></i>
          </div>
          <h2 className="text-white font-bold text-xl mb-2">접근 권한 없음</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            관리자 페이지는 관리자 계정만 접근할 수 있습니다.<br />
            권한이 필요하시면 시스템 관리자에게 문의하세요.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-5 py-2.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] text-gray-300 text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
          >
            <i className="ri-arrow-left-line mr-1.5"></i>
            대시보드로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const sidebarWidth = collapsed ? "ml-16" : "ml-60";

  const renderContent = () => {
    switch (activeTab) {
      case "users": return <UserManagementTab />;
      case "charges": return <CreditHistoryTab />;
      case "notices": return <NoticesTab />;
      case "sender-review": return <SenderReviewTab />;
      case "send-stats": return <SendStatsTab />;
      case "api-keys": return <ApiKeysPlaceholder />;
      case "carrier-numbers": return <AdminCarrierNumberTab />;
      case "carrier-test-review": return <AdminCarrierTestReviewTab />;
      case "system": return <SystemPlaceholder />;
      default: return <AdminOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <AdminSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen`}>
        {/* Top bar */}
        <header className="h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <span className="text-rose-400 text-xs font-semibold bg-rose-500/10 border border-rose-500/20 px-2.5 py-1 rounded-full whitespace-nowrap">
              ADMIN
            </span>
            <h1 className="text-white font-semibold text-base">{tabLabels[activeTab]}</h1>
          </div>
          <div className="flex items-center gap-3">
            <SenderBalanceIndicator state={balance} />
            <div className="h-4 w-px bg-white/[0.1]"></div>
            <AdminNotificationBell />
            <div className="h-4 w-px bg-white/[0.1]"></div>
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <i className="ri-shield-check-line text-emerald-400"></i>
              <span className="text-emerald-400">시스템 정상</span>
            </div>
            <div className="h-4 w-px bg-white/[0.1]"></div>
            <span className="text-gray-500 text-xs">2026.05.16 16:30</span>
          </div>
        </header>

        {/* 발신 계정 잔액 경고 배너 */}
        <SenderBalanceBanner state={balance} />

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}